import { AccountIcon, CloseIcon, ManscapedLogo, MenuIcon } from "@theme/assets";
import { NavLink } from "@theme/index";
import { m } from "framer-motion";
import { useAtom } from "jotai";
import { Fragment } from "react";
import ReactFocusLock from "react-focus-lock";

import {
  Container,
  Flex,
  IconButton,
  NavCartLink,
  NavDropdown,
  NavMobileDrawer,
  NavSearchButton,
  StrapiLink,
  ThemeWrapper,
  drawerOpenAtom,
  getThemeValue,
} from "@theme/components";

import type { NavigationDataProps } from "@theme/types";
import type { FC } from "react";

interface NavMenuBarProps {
  navData: NavigationDataProps;
  isCheckout?: boolean;
}

const desktopOnly = { display: ["none", null, null, "flex"] };

// NOTE: this component is shadowed. All updates must be done in both gatsby-theme and gatsby-site
export const NavMenuBar: FC<NavMenuBarProps> = ({ navData, isCheckout }) => {
  const [isDrawerOpen, setIsDrawerOpen] = useAtom(drawerOpenAtom);

  return (
    <m.nav
      initial="show"
      sx={{
        bg: "grey700",
        width: "full",
        zIndex: 2,
      }}
    >
      <Container
        variant="wide"
        as="menu"
        sx={{
          height: (t) => getThemeValue(t, "sizes.navBarHeight"),
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          position: "relative",
        }}
      >
        {!isCheckout && (
          // returnFocus must be false because it causes the focus to go to the skip to main content button
          <ReactFocusLock disabled={!isDrawerOpen} returnFocus={false}>
            <IconButton
              data-testid="button-menu-testid-2"
              data-click-outside-exempt="mobile-menu"
              aria-label="Toggle mobile menu"
              aria-expanded={isDrawerOpen}
              variant="iconDark"
              onClick={() => setIsDrawerOpen(!isDrawerOpen)}
              sx={{
                display: ["flex", null, null, "none"],
                zIndex: 1,
                transform: (t) => `translateX(-${getThemeValue(t, "space.s200")})`,
                padding: 0,
                ":hover": {
                  backgroundColor: "transparent",
                },
              }}
            >
              {isDrawerOpen ? (
                <CloseIcon
                  sx={{ color: "white !important", width: "iconSizeBase", height: "auto" }}
                />
              ) : (
                <MenuIcon
                  sx={{ color: "white !important", width: "iconSizeBase", height: "auto" }}
                />
              )}
            </IconButton>
            <NavMobileDrawer navData={navData} />
          </ReactFocusLock>
        )}
        <NavLink
          variant="navbarItem"
          to={navData.homeUrl}
          aria-label="Go to Homepage"
          sx={{ mr: "auto" }}
        >
          <ManscapedLogo
            sx={{
              color: "white",
              width: [100, 150, null, null],
              height: [18, 27, null, null],
              padding: 0,
            }}
          />
        </NavLink>
        {!isCheckout && (
          <Flex
            sx={{
              height: "full",
              alignItems: "center",
              gap: ["s700"],
              mr: (t) => `-${getThemeValue(t, "space.s200")}`,
            }}
          >
            {navData?.dropdownLinks.map(({ links, title }) => (
              <Fragment key={title}>
                {links.length > 1 ? (
                  // If there's more than one link, render as a dropdown
                  <NavDropdown
                    type="link"
                    title={title}
                    ariaLabel={`${title} dropdown`}
                    navData={navData}
                    sx={{
                      'div[data-dropdown-menu="true"]': {
                        width: ["100%", "auto", null],
                      },
                      ...desktopOnly,
                    }}
                  />
                ) : (
                  // If there's only one link, render as a direct link
                  <StrapiLink
                    linkVariant="textLinkPrimaryDark"
                    sx={{
                      variant: "text.sansBody01Regular",
                      textDecoration: "none",
                      ...desktopOnly,
                    }}
                    ariaLabel={links[0]?.label ?? ""}
                    isBlog={navData.isBlog}
                    {...links[0]}
                  />
                )}
              </Fragment>
            ))}
            <Flex sx={{ alignItems: "center", gap: ["s50", null, "s400"] }}>
              {navData.languages.length > 1 && (
                <ThemeWrapper theme="DARK">
                  <NavDropdown
                    type="language"
                    ariaLabel={`Language Dropdown, current language is ${navData.currentLanguage}`}
                    navData={navData}
                    sx={{
                      'button[data-dropdown-button="true"]': {
                        textTransform: "uppercase",
                      },
                      ...desktopOnly,
                    }}
                  />
                </ThemeWrapper>
              )}
              {navData.isAuthenticated ? (
                <ThemeWrapper theme="DARK">
                  <NavDropdown
                    type="account"
                    ariaLabel="Account Dropdown"
                    accountLabel={navData.accountLabel}
                    accountLinks={navData.accountLinks}
                    signOut={navData.signOut}
                    navData={navData}
                    pointsModule={null}
                  />
                </ThemeWrapper>
              ) : (
                <ThemeWrapper theme="DARK">
                  <NavLink
                    to={navData.loginLink.to ?? ""}
                    variant="navbarItem"
                    data-testid="nav-login-link"
                    aria-label={navData.loginLink.label}
                  >
                    <AccountIcon sx={{ color: "grey300" }} />
                  </NavLink>
                </ThemeWrapper>
              )}
              <NavCartLink navData={navData} />
              <NavSearchButton
                theme="DARK"
                includeShim
                sx={{
                  display: ["inline-block", null, null, "none"],
                  svg: { color: "textLevel3" },
                }}
              />
            </Flex>
          </Flex>
        )}
      </Container>
    </m.nav>
  );
};
